// Grid line recalculation utilities for PdfTableViewer - Multi-page architecture
import {
  USE_SERVER_SIDE_GRIDLINES,
  COLUMN_ADDITION_MULTI_PAGE,
  COLUMN_MOVEMENT_MULTI_PAGE,
} from "../config/gridLinesConfig";
import {
  calculateWordCenter,
  determineCellPosition,
  sortWordsByPosition,
} from "./gridCalculationUtils";
import { detectGridLineSplitIndex } from "./gridLineDetectionUtils";

// ============================================================================
// CONSTANTS
// ============================================================================

/** Movement detection threshold in pixels for columns */
const COLUMN_MOVEMENT_THRESHOLD = 2;

/** Movement detection threshold in pixels for rows */
const ROW_MOVEMENT_THRESHOLD = 1;

/** Line position tolerance for matching */
const LINE_POSITION_TOLERANCE = 1;

/** Default names for new columns */
const NEW_COLUMN_PREFIX = "NewColumn_";
const DEFAULT_COLUMN_PREFIX = "Column ";

// ============================================================================
// DATA STRUCTURES
// ============================================================================

/**
 * Represents a change operation on grid lines
 */
class GridLineChange {
  constructor(
    type,
    pageNum,
    direction,
    index,
    oldValue,
    newValue,
    splitColumn = null
  ) {
    this.type = type; // 'added', 'removed', 'moved'
    this.pageNum = pageNum;
    this.direction = direction; // 'vertical', 'horizontal'
    this.index = index;
    this.oldValue = oldValue;
    this.newValue = newValue;
    this.splitColumn = splitColumn; // For column splits
  }
}

// ============================================================================
// UTILITY FUNCTIONS - Cell Key Parsing & Validation
// ============================================================================

/**
 * Parse a cell key into row and column indices
 * @param {string} cellKey - Cell key in format "row_col"
 * @returns {Object} Object with rowIndex, colIndex, rowStr, colStr
 */
const parseCellKey = (cellKey) => {
  const [rowStr, colStr] = cellKey.split("_");
  return {
    rowIndex: parseInt(rowStr),
    colIndex: parseInt(colStr),
    rowStr,
    colStr,
  };
};

/**
 * Check if a string is a valid cell key (format: "row_col")
 */
const isValidCellKey = (cellKey) => cellKey.match(/^\d+_\d+$/);

/**
 * Check if cell is on specified page
 */
const isCellOnPage = (cellData, page) => {
  return (
    cellData &&
    Array.isArray(cellData) &&
    cellData[0] &&
    cellData[0].page === page
  );
};

/**
 * Check if table uses server-side coordinate system
 */
const isServerSideTable = (selectedTable) => {
  return USE_SERVER_SIDE_GRIDLINES && selectedTable?.grid_lines;
};

/**
 * Get page-specific data from multi-page structures
 */
const getPageData = (multiPageData, pageNum) => {
  if (!multiPageData) return null;
  return multiPageData[pageNum] || null;
};

// ============================================================================
// UTILITY FUNCTIONS - Multi-page Table Operations
// ============================================================================

/**
 * Calculate page row offset for multi-page tables
 * Maps page-local row indices to global table row indices
 */
const calculatePageRowOffset = (selectedTable, currentPage) => {
  let minCellRowIndex = null;

  // Find the first DATA cell on the current page (skip header row 0)
  const cellKeys = Object.keys(selectedTable).filter(isValidCellKey);

  for (const cellKey of cellKeys) {
    const cellData = selectedTable[cellKey];
    if (isCellOnPage(cellData, currentPage)) {
      const { rowIndex } = parseCellKey(cellKey);

      // Skip header row (cell key 0)
      if (rowIndex === 0) continue;

      // Find the minimum DATA cell row index on this page
      if (minCellRowIndex === null || rowIndex < minCellRowIndex) {
        minCellRowIndex = rowIndex;
      }
    }
  }

  // Cell row index to tableData row index: subtract 1 for header row
  return minCellRowIndex !== null ? minCellRowIndex - 1 : 0;
};

/**
 * Find tableData row index for a selectedTable cell
 * Maps selectedTable row indices (0=header, 1=first data row) to tableData.rows indices (0=first data row)
 */
const findTableRowIndexForCell = (
  cellKey,
  selectedTable,
  tableData,
  currentPage
) => {
  const cellData = selectedTable[cellKey];
  if (!cellData || !Array.isArray(cellData) || !cellData[0]) {
    return null;
  }

  const { rowIndex: selectedTableRowIndex } = parseCellKey(cellKey);

  // Header row - no tableData row index
  if (selectedTableRowIndex === 0) {
    return null;
  }

  // Simple mapping: selectedTable row N maps to tableData.rows[N-1]
  const tableRowIndex = selectedTableRowIndex - 1;

  if (tableRowIndex >= 0 && tableRowIndex < tableData.rows.length) {
    return tableRowIndex;
  }

  return null;
};

// ============================================================================
// UTILITY FUNCTIONS - Change Detection & Filtering
// ============================================================================

/**
 * Determine if a change should be included based on operation type and page restrictions
 */
const shouldIncludeChange = (
  change,
  currentPage,
  limitColumnAdditionsToCurrentPage,
  limitColumnMovementsToCurrentPage
) => {
  const { direction, type, pageNum } = change;

  if (direction === "vertical") {
    if (type === "added")
      return limitColumnAdditionsToCurrentPage ? pageNum === currentPage : true;
    if (type === "moved")
      return limitColumnMovementsToCurrentPage ? pageNum === currentPage : true;
  }

  if (direction === "horizontal") {
    // Row operations are always page-specific
    return pageNum === currentPage;
  }

  return pageNum === currentPage;
};

/**
 * Determine target pages for word extraction based on change types and restrictions
 */
const getTargetPages = (
  changes,
  currentPage,
  limitColumnAdditionsToCurrentPage,
  limitColumnMovementsToCurrentPage
) => {
  const hasColumnAdditions = changes.some(
    (c) => c.direction === "vertical" && c.type === "added"
  );
  const hasColumnMovements = changes.some(
    (c) => c.direction === "vertical" && c.type === "moved"
  );

  if (hasColumnAdditions && limitColumnAdditionsToCurrentPage) {
    return [currentPage];
  }

  if (
    hasColumnMovements &&
    limitColumnMovementsToCurrentPage &&
    !hasColumnAdditions
  ) {
    return [currentPage];
  }

  return null; // All pages
};

/**
 * Validate required parameters for recalculation
 */
const validateRecalculationInputs = (
  dynamicGridLines,
  currentPage,
  selectedTable,
  tableData,
  originalGridLinesData,
  newGridLines
) => {
  if (!dynamicGridLines?.[currentPage] || !selectedTable || !tableData) {
    return { isValid: false, result: null };
  }

  if (!originalGridLinesData || !newGridLines) {
    return { isValid: false, result: tableData };
  }

  return { isValid: true };
};

// ============================================================================
// STRUCTURAL CHANGE DETECTION (ADDITIONS/REMOVALS)
// ============================================================================

/**
 * Extract coordinate from a grid line change
 */
const getChangeCoordinate = (change) => {
  if (change.type === "added" && change.newValue) {
    return change.direction === "vertical"
      ? change.newValue.x0
      : change.newValue.y0;
  }
  if (change.type === "removed" && change.oldValue) {
    return change.direction === "vertical"
      ? change.oldValue.x0
      : change.oldValue.y0;
  }
  return change.index;
};

/**
 * Create a unique key for a change based on type and coordinate
 */
const createChangeKey = (change) => {
  const coordinate = getChangeCoordinate(change);
  return `${change.type}_${coordinate}`;
};

/**
 * Process changes and return unique changes with updated count
 */
const processChanges = (changes, initialCount) => {
  const uniqueChanges = new Map();
  let newCount = initialCount;

  changes.forEach((change) => {
    const changeKey = createChangeKey(change);

    if (!uniqueChanges.has(changeKey)) {
      uniqueChanges.set(changeKey, change);
      if (change.type === "added") {
        newCount++;
      } else if (change.type === "removed") {
        newCount--;
      }
    }
  });

  return { uniqueChanges, newCount };
};

/**
 * Detect added and removed lines in a single direction (vertical or horizontal)
 * Only handles structural changes, not movements
 */
const detectAddedAndRemovedLines = (
  originalLines,
  newLines,
  direction,
  pageNum,
  selectedTable,
  pdfPageDimensions
) => {
  const changes = [];

  // Sort lines by position for consistent comparison
  const originalSorted = [...originalLines].sort((a, b) =>
    direction === "vertical" ? a.x0 - b.x0 : a.y0 - b.y0
  );
  const newSorted = [...newLines].sort((a, b) =>
    direction === "vertical" ? a.x0 - b.x0 : a.y0 - b.y0
  );

  // Detect added lines
  if (newSorted.length > originalSorted.length) {
    const addedLines = newSorted.filter((newLine) => {
      const position = direction === "vertical" ? newLine.x0 : newLine.y0;
      return !originalSorted.some((origLine) => {
        const origPosition =
          direction === "vertical" ? origLine.x0 : origLine.y0;
        return Math.abs(position - origPosition) < LINE_POSITION_TOLERANCE;
      });
    });

    addedLines.forEach((line) => {
      const actualIndex = newSorted.findIndex(
        (sortedLine) =>
          Math.abs(
            (direction === "vertical" ? sortedLine.x0 : sortedLine.y0) -
              (direction === "vertical" ? line.x0 : line.y0)
          ) < LINE_POSITION_TOLERANCE
      );
      const position = direction === "vertical" ? line.x0 : line.y0;
      const splitIndex = detectGridLineSplitIndex(
        position,
        direction,
        selectedTable,
        pdfPageDimensions,
        pageNum
      );

      changes.push(
        new GridLineChange(
          "added",
          parseInt(pageNum),
          direction,
          actualIndex,
          null,
          line,
          splitIndex
        )
      );
    });
  }

  // Detect removed lines
  if (originalSorted.length > newSorted.length) {
    const removedCount = originalSorted.length - newSorted.length;
    for (let i = 0; i < removedCount; i++) {
      const removedIndex = newSorted.length + i;
      changes.push(
        new GridLineChange(
          "removed",
          parseInt(pageNum),
          direction,
          removedIndex,
          originalSorted[removedIndex],
          null
        )
      );
    }
  }

  return changes;
};

/**
 * Detect structural changes (additions/removals) between original and new grid lines across all pages
 * Used for column and row additions, not for movements
 */
const detectStructuralGridLineChanges = (
  originalGridLines,
  newGridLines,
  selectedTable,
  pdfPageDimensions
) => {
  const changes = [];

  const allPages = new Set([
    ...Object.keys(originalGridLines || {}),
    ...Object.keys(newGridLines || {}),
  ]);

  allPages.forEach((pageNum) => {
    const originalPage = getPageData(originalGridLines, pageNum);
    const newPage = getPageData(newGridLines, pageNum);

    if (!originalPage || !newPage) return;

    const verticalChanges = detectAddedAndRemovedLines(
      originalPage.vertical || [],
      newPage.vertical || [],
      "vertical",
      pageNum,
      selectedTable,
      pdfPageDimensions
    );
    changes.push(...verticalChanges);

    const horizontalChanges = detectAddedAndRemovedLines(
      originalPage.horizontal || [],
      newPage.horizontal || [],
      "horizontal",
      pageNum,
      selectedTable,
      pdfPageDimensions
    );
    changes.push(...horizontalChanges);
  });

  return changes;
};

// ============================================================================
// STRUCTURAL CHANGE PROCESSING (TABLE EXPANSION)
// ============================================================================

/**
 * Add UI metadata for split operations
 */
const addUIMetadata = (result, changes) => {
  const columnAdditions = changes.filter(
    (c) => c.direction === "vertical" && c.type === "added"
  );
  if (columnAdditions.length > 0) {
    result.splitColumnIndex = columnAdditions[0].splitColumn;
  }

  const rowAdditions = changes.filter(
    (c) => c.direction === "horizontal" && c.type === "added"
  );
  if (rowAdditions.length > 0) {
    result.splitRowIndex = rowAdditions[0].splitColumn;
  }

  return result;
};

/**
 * Check if a word should be processed for column addition
 */
const shouldProcessWordForColumnAddition = (
  word,
  columnAddition,
  currentPage
) => {
  if (!columnAddition || !word.originalCellKey) return false;

  const { colIndex } = parseCellKey(word.originalCellKey);

  if (colIndex !== columnAddition.splitColumn) return false;
  if (!COLUMN_ADDITION_MULTI_PAGE && word.page !== currentPage) return false;

  return true;
};

/**
 * Check if a word should be processed for row addition
 */
const shouldProcessWordForRowAddition = (word, rowAddition, currentPage) => {
  if (!rowAddition || !word.originalCellKey) return false;

  const { rowIndex } = parseCellKey(word.originalCellKey);

  if (rowIndex !== rowAddition.splitColumn) return false;
  if (word.page !== currentPage) return false;

  return true;
};

/**
 * Unified helper to calculate new position for column or row addition
 * Eliminates duplication between column and row addition logic
 *
 * @param {Object} word - Word object with originalCellKey and position data
 * @param {Object} addition - GridLineChange object representing the addition
 * @param {Object} pdfPageDimensions - PDF page dimensions for coordinate conversion
 * @param {boolean} isColumn - True for column addition, false for row addition
 * @returns {Object} Object with rowIndex and colIndex for the word's new position
 */
const calculateAdditionPosition = (
  word,
  addition,
  pdfPageDimensions,
  isColumn
) => {
  const { rowIndex: origRowIndex, colIndex: origColIndex } = parseCellKey(
    word.originalCellKey
  );
  const splitIndex = addition.splitColumn;
  const splitLinePosition = isColumn
    ? addition.newValue?.x0
    : addition.newValue?.y0;

  if (isColumn) {
    const rowIndex = origRowIndex; // Keep same row for column splits

    if (origColIndex === splitIndex && splitLinePosition !== undefined) {
      const wordCenter = calculateWordCenter(word, pdfPageDimensions);
      const shouldMoveToNewPosition = isColumn
        ? wordCenter.x >= splitLinePosition
        : wordCenter.y >= splitLinePosition;

      return {
        rowIndex,
        colIndex: shouldMoveToNewPosition ? splitIndex + 1 : splitIndex,
      };
    }

    const colIndex =
      origColIndex > splitIndex ? origColIndex + 1 : origColIndex;
    return { rowIndex, colIndex };
  } else {
    const colIndex = origColIndex; // Keep same column for row splits

    if (origRowIndex === splitIndex && splitLinePosition !== undefined) {
      const wordCenter = calculateWordCenter(word, pdfPageDimensions);
      return {
        rowIndex:
          wordCenter.y >= splitLinePosition ? splitIndex : splitIndex - 1,
        colIndex,
      };
    }

    const rowIndex =
      origRowIndex >= splitIndex ? origRowIndex + 1 : origRowIndex;
    return { rowIndex, colIndex };
  }
};

/**
 * Calculate new position for column addition
 */
const calculateColumnAdditionPosition = (
  word,
  columnAddition,
  pdfPageDimensions
) => {
  return calculateAdditionPosition(
    word,
    columnAddition,
    pdfPageDimensions,
    true
  );
};

/**
 * Calculate new position for row addition
 */
const calculateRowAdditionPosition = (word, rowAddition, pdfPageDimensions) => {
  return calculateAdditionPosition(word, rowAddition, pdfPageDimensions, false);
};

/**
 * Process each valid cell in a selected table
 */
const forEachValidCell = (selectedTable, callback) => {
  Object.keys(selectedTable).forEach((cellKey) => {
    if (!isValidCellKey(cellKey)) return;
    const cellCoords = parseCellKey(cellKey);
    callback(cellKey, cellCoords, selectedTable[cellKey]);
  });
};

/**
 * Extract all words from a table that belong to specific pages
 */
const extractWordsFromPages = (selectedTable, targetPages = null) => {
  const words = [];

  forEachValidCell(selectedTable, (cellKey, _, cellData) => {
    if (!Array.isArray(cellData)) return;

    cellData.forEach((cellInfo) => {
      if (!cellInfo.words || !Array.isArray(cellInfo.words)) return;
      if (targetPages && !targetPages.includes(cellInfo.page)) return;

      cellInfo.words.forEach((word) => {
        words.push({
          ...word,
          originalCellKey: cellKey,
          page: cellInfo.page,
        });
      });
    });
  });

  return words;
};

/**
 * Get affected cells for a set of changes
 */
const getAffectedCells = (changes, selectedTable, limitToPages = null) => {
  const affectedCells = new Set();

  changes.forEach((change) => {
    // If limiting to specific pages, skip changes from other pages
    if (limitToPages && !limitToPages.includes(change.pageNum)) return;

    // For each change, find cells that are affected
    forEachValidCell(selectedTable, (cellKey, cellCoords, cellData) => {
      const { rowIndex, colIndex } = cellCoords;

      // Check if this cell is affected by the change
      let isAffected = false;

      if (change.direction === "vertical") {
        if (change.type === "added" && change.splitColumn !== null) {
          isAffected = colIndex >= change.splitColumn;
        } else if (change.type === "moved") {
          isAffected =
            colIndex === change.index || colIndex === change.index + 1;
        }
      } else {
        if (change.type === "added" && change.splitColumn !== null) {
          isAffected = rowIndex >= change.splitColumn;
        } else if (change.type === "moved") {
          isAffected =
            rowIndex === change.index || rowIndex === change.index + 1;
        }
      }

      if (isAffected) {
        // Column splits affect all pages
        if (change.direction === "vertical" && change.type === "added") {
          affectedCells.add(cellKey);
        } else {
          // Column movements and row changes only affect the specific page
          if (Array.isArray(cellData)) {
            cellData.forEach((cellInfo) => {
              if (cellInfo.page === change.pageNum) {
                affectedCells.add(cellKey);
              }
            });
          }
        }
      }
    });
  });

  return affectedCells;
};

/**
 * Expand headers with new columns
 * Handles inserting new column headers when grid lines are added
 *
 * @param {Array} originalHeaders - Original table headers
 * @param {Map} columnChanges - Map of unique column changes
 * @param {Object} selectedTable - Selected table for server-side check
 * @returns {Array} Expanded headers array
 */
const expandHeaders = (originalHeaders, columnChanges, selectedTable) => {
  const isServerSide = isServerSideTable(selectedTable);
  // Use server-side headers if available, otherwise use original headers
  const newHeaders =
    isServerSide && selectedTable.headers
      ? [...selectedTable.headers]
      : [...originalHeaders];

  // Handle column additions in headers
  columnChanges.forEach((change) => {
    if (change.type === "added" && change.splitColumn !== null) {
      const insertPosition = change.splitColumn + 1;
      const newColumnName = `${NEW_COLUMN_PREFIX}${change.splitColumn + 1}`;
      newHeaders.splice(insertPosition, 0, newColumnName);
    }
  });

  return newHeaders;
};

/**
 * Expand rows with new columns
 * Adds empty cells to existing rows when new columns are inserted
 *
 * @param {Array} originalRows - Original table rows
 * @param {Map} columnChanges - Map of unique column changes
 * @param {number} targetColumnCount - Target number of columns after expansion
 * @returns {Array} Expanded rows array
 */
const expandRowsWithColumns = (
  originalRows,
  columnChanges,
  targetColumnCount
) => {
  return originalRows.map((row) => {
    const newRow = [...row];

    // Handle column additions - insert empty cells
    columnChanges.forEach((change) => {
      if (change.type === "added" && change.splitColumn !== null) {
        const insertPosition = change.splitColumn + 1;
        newRow.splice(insertPosition, 0, "");
      }
    });

    // Ensure row has enough columns
    while (newRow.length < targetColumnCount) {
      newRow.push("");
    }

    return newRow;
  });
};

/**
 * Add new rows to table structure
 * Inserts new empty rows when horizontal grid lines are added
 *
 * @param {Array} rows - Current table rows
 * @param {Array} rowChanges - Array of row changes
 * @param {number} columnCount - Number of columns in each row
 * @returns {Array} Rows array with new rows inserted
 */
const addNewRows = (rows, rowChanges, columnCount) => {
  const newRows = [...rows];

  rowChanges.forEach((change) => {
    if (change.type === "added" && change.splitColumn !== null) {
      const insertPosition = change.splitColumn;
      const newRow = new Array(columnCount).fill("");
      newRows.splice(insertPosition, 0, newRow);
    }
  });

  return newRows;
};

/**
 * Create new table structure for structural changes (added/removed rows and columns only)
 * This function handles expanding the table when new grid lines are added
 */
const createNewTableStructure = (originalTableData, changes, selectedTable) => {
  // Process changes and calculate new dimensions
  const columnChanges = changes.filter((c) => c.direction === "vertical");
  const rowChanges = changes.filter((c) => c.direction === "horizontal");

  const {
    uniqueChanges: uniqueColumnChanges,
    newCount: newColumnCount,
  } = processChanges(columnChanges, originalTableData.headers?.length || 0);

  const { newCount: newRowCount } = processChanges(
    rowChanges,
    originalTableData.rows?.length || 0
  );

  // Expand headers with new columns
  let newHeaders = expandHeaders(
    originalTableData.headers || [],
    uniqueColumnChanges,
    selectedTable
  );
  // Ensure we have enough headers
  while (newHeaders.length < newColumnCount) {
    newHeaders.push(`${DEFAULT_COLUMN_PREFIX}${newHeaders.length + 1}`);
  }

  // Expand rows with new columns
  let newRows = expandRowsWithColumns(
    originalTableData.rows,
    uniqueColumnChanges,
    newColumnCount
  );

  // Add new rows for horizontal grid line additions
  newRows = addNewRows(newRows, rowChanges, newColumnCount);

  // Ensure we have enough rows
  while (newRows.length < newRowCount) {
    newRows.push(new Array(newColumnCount).fill(""));
  }

  return {
    headers: newHeaders,
    rows: newRows,
  };
};

/**
 * Redistribute words for structural changes (column/row additions)
 * Handles word placement when new grid lines are added and table structure changes
 */
const redistributeWords = (
  words,
  newGridLines,
  changes,
  newTableStructure,
  selectedTable,
  pdfPageDimensions,
  currentPage
) => {
  const newAssignments = {};

  // Find if we have additions to handle differently
  const columnAddition = changes.find(
    (c) => c.direction === "vertical" && c.type === "added"
  );
  const rowAddition = changes.find(
    (c) => c.direction === "horizontal" && c.type === "added"
  );

  words.forEach((word) => {
    // Filter words based on operation type
    if (
      columnAddition &&
      !shouldProcessWordForColumnAddition(word, columnAddition, currentPage)
    ) {
      return;
    }

    if (
      rowAddition &&
      !shouldProcessWordForRowAddition(word, rowAddition, currentPage)
    ) {
      return;
    }

    const pageGridLines = getPageData(newGridLines, word.page);
    if (!pageGridLines) return;

    // Determine new cell position based on operation type
    let rowIndex, colIndex;

    if (columnAddition && word.originalCellKey) {
      ({ rowIndex, colIndex } = calculateColumnAdditionPosition(
        word,
        columnAddition,
        pdfPageDimensions
      ));
    } else if (rowAddition && word.originalCellKey) {
      ({ rowIndex, colIndex } = calculateRowAdditionPosition(
        word,
        rowAddition,
        pdfPageDimensions
      ));
    } else {
      // For other operations, recalculate both row and column normally
      const wordCenter = calculateWordCenter(word, pdfPageDimensions);
      const sortedHorizontal = [...(pageGridLines.horizontal || [])].sort(
        (a, b) => a.y0 - b.y0
      );
      const sortedVertical = [...(pageGridLines.vertical || [])].sort(
        (a, b) => a.x0 - b.x0
      );
      const result = determineCellPosition(
        wordCenter,
        sortedHorizontal,
        sortedVertical
      );
      rowIndex = result.rowIndex;
      colIndex = result.colIndex;
    }

    const cellKey = `${rowIndex}_${colIndex}`;

    if (!newAssignments[cellKey]) {
      newAssignments[cellKey] = [];
    }

    newAssignments[cellKey].push(word);
  });

  return newAssignments;
};

/**
 * Get row index for word assignment based on table type and operation
 * Handles different index mapping for server-side vs client-side tables and column additions
 *
 * @param {number} rowIndex - Source row index from word assignment
 * @param {number} colIndex - Column index
 * @param {boolean} isServerSide - Whether table uses server-side coordinate system
 * @param {boolean} isColumnAddition - Whether this is a column addition operation
 * @param {Object} result - Result table structure to validate against
 * @returns {number|string|null} Target row index ('header' for header row, number for data row, null if invalid)
 */
const getRowIndexForAssignment = (
  rowIndex,
  colIndex,
  isServerSide,
  isColumnAddition,
  result
) => {
  if (isServerSide) {
    const displayRowIndex = isColumnAddition ? rowIndex - 1 : rowIndex;
    if (
      displayRowIndex >= 0 &&
      displayRowIndex < result.rows.length &&
      colIndex < result.rows[displayRowIndex].length
    ) {
      return displayRowIndex;
    }
    return null;
  } else {
    if (rowIndex === 0) {
      return "header";
    }
    const dataRowIndex = rowIndex - 1;
    if (
      dataRowIndex < result.rows.length &&
      colIndex < result.rows[dataRowIndex].length
    ) {
      return dataRowIndex;
    }
    return null;
  }
};

/**
 * Apply word assignments to table structure for structural changes
 * Maps words to their new positions when columns/rows are added
 */
const applyWordAssignments = (
  newTableStructure,
  wordAssignments,
  selectedTable,
  changes = []
) => {
  const isServerSide = isServerSideTable(selectedTable);
  // Check if this is a column addition (needs different index mapping)
  const isColumnAddition = changes.some(
    (c) => c.direction === "vertical" && c.type === "added"
  );

  const result = {
    headers: [...newTableStructure.headers],
    rows: newTableStructure.rows.map((row) => [...row]),
  };

  // Apply word assignments to table structure
  Object.keys(wordAssignments).forEach((cellKey) => {
    const { rowIndex, colIndex } = parseCellKey(cellKey);
    const words = sortWordsByPosition(wordAssignments[cellKey]);
    const text = words
      .map((w) => w.text)
      .join(" ")
      .trim();

    const targetRowIndex = getRowIndexForAssignment(
      rowIndex,
      colIndex,
      isServerSide,
      isColumnAddition,
      result
    );

    if (targetRowIndex === "header") {
      if (colIndex < result.headers.length) {
        result.headers[colIndex] = text;
      }
    } else if (targetRowIndex !== null) {
      result.rows[targetRowIndex][colIndex] = text;
    }
  });

  return result;
};

// ============================================================================
// UNIFIED GRID LINE MOVEMENT FUNCTIONS
// ============================================================================

/**
 * Helper function to detect moved lines in a single direction
 * Reduces code duplication between vertical and horizontal detection
 *
 * @param {Array} originalLines - Original grid lines
 * @param {Array} newLines - New grid lines after movement
 * @param {string} direction - 'vertical' or 'horizontal'
 * @param {number} threshold - Movement threshold in pixels
 * @returns {Array} Array of moved line objects with index, originalPos, newPos
 */
const detectMovedLinesInDirection = (
  originalLines,
  newLines,
  direction,
  threshold
) => {
  if (!originalLines || !newLines) return [];

  const movedLines = [];
  const minLength = Math.min(originalLines.length, newLines.length);
  const posKey = direction === "vertical" ? "x0" : "y0";

  for (let i = 0; i < minLength; i++) {
    const originalPos = originalLines[i]?.[posKey];
    const newPos = newLines[i]?.[posKey];
    const movement = Math.abs(originalPos - newPos);

    if (
      originalPos !== undefined &&
      newPos !== undefined &&
      movement > threshold
    ) {
      movedLines.push({
        index: i,
        originalPos,
        newPos,
      });
    }
  }

  return movedLines;
};

/**
 * Detect moved grid lines by comparing positions
 * Identifies which vertical (column) and horizontal (row) grid lines have moved
 */
const detectMovedGridLines = (originalGridLines, newGridLines) => {
  const movedVertical = detectMovedLinesInDirection(
    originalGridLines.vertical,
    newGridLines.vertical,
    "vertical",
    COLUMN_MOVEMENT_THRESHOLD
  );

  const movedHorizontal = detectMovedLinesInDirection(
    originalGridLines.horizontal,
    newGridLines.horizontal,
    "horizontal",
    ROW_MOVEMENT_THRESHOLD
  );

  const movedPositions = {};
  movedVertical.forEach(
    (m) => (movedPositions[`vertical_${m.index}`] = m.newPos)
  );
  movedHorizontal.forEach(
    (m) => (movedPositions[`horizontal_${m.index}`] = m.newPos)
  );

  return {
    vertical: movedVertical,
    horizontal: movedHorizontal,
    positions: movedPositions,
    hasMovement: movedVertical.length > 0 || movedHorizontal.length > 0,
  };
};

/**
 * Helper function to determine affected columns or rows from moved grid lines
 * Reduces code duplication between column and row detection
 *
 * @param {Array} movements - Array of moved grid line objects
 * @param {boolean} hasEdgeInArray - Whether grid line array includes edge (left/top)
 * @param {number} maxIndex - Maximum valid index (headers.length or rows.length)
 * @returns {Set} Set of affected indices
 */
const getAffectedIndices = (movements, hasEdgeInArray, maxIndex) => {
  const affectedIndices = new Set();

  movements.forEach((movement) => {
    const gridLineArrayIndex = movement.index;
    let leftOrTopIndex, rightOrBottomIndex;

    if (hasEdgeInArray) {
      leftOrTopIndex = gridLineArrayIndex - 1;
      rightOrBottomIndex = gridLineArrayIndex;
    } else {
      leftOrTopIndex = gridLineArrayIndex;
      rightOrBottomIndex = gridLineArrayIndex + 1;
    }

    if (leftOrTopIndex >= 0 && leftOrTopIndex < maxIndex) {
      affectedIndices.add(leftOrTopIndex);
    }
    if (rightOrBottomIndex >= 0 && rightOrBottomIndex < maxIndex) {
      affectedIndices.add(rightOrBottomIndex);
    }
  });

  return affectedIndices;
};

/**
 * Find affected table regions from moved grid lines
 * Identifies which columns and rows are affected by moved grid lines
 */
const findAffectedTableRegions = (
  movedGridLines,
  tableData,
  selectedTable,
  currentPage,
  totalVerticalGridLines = 0,
  totalHorizontalGridLines = 0
) => {
  // Calculate page row offset for multi-page tables
  const pageRowOffset = calculatePageRowOffset(selectedTable, currentPage);

  // Determine grid line array structure
  // If gridLines.length == headers.length + 1, then array includes left edge
  // If gridLines.length == headers.length, then array excludes edges
  const hasLeftEdgeInArray =
    totalVerticalGridLines === tableData.headers.length + 1;
  const hasTopEdgeInArray =
    totalHorizontalGridLines === tableData.rows.length + 1;

  // Find affected columns from moved vertical lines
  const affectedColumns = getAffectedIndices(
    movedGridLines.vertical,
    hasLeftEdgeInArray,
    tableData.headers.length
  );

  // Find affected rows from moved horizontal lines (page-local)
  const pageLocalAffectedRows = getAffectedIndices(
    movedGridLines.horizontal,
    hasTopEdgeInArray,
    tableData.rows.length
  );

  // Convert page-local row indices to global row indices for multi-page tables
  const affectedRows = new Set();
  pageLocalAffectedRows.forEach((pageLocalRow) => {
    const globalRow = pageLocalRow + pageRowOffset;
    if (globalRow >= 0 && globalRow < tableData.rows.length) {
      affectedRows.add(globalRow);
    }
  });

  // Convert affected row indices to row IDs for modal display
  // Row ID is always the first column of the table data
  const affectedRowIds = [];
  affectedRows.forEach((rowIndex) => {
    if (
      rowIndex >= 0 &&
      rowIndex < tableData.rows.length &&
      tableData.rows[rowIndex]
    ) {
      const rowId = tableData.rows[rowIndex][0];
      if (rowId) affectedRowIds.push(rowId);
    }
  });

  return {
    columns: Array.from(affectedColumns),
    rows: Array.from(affectedRows),
    rowIds: affectedRowIds,
  };
};

/**
 * Helper function to extract words from cells based on filter criteria
 * Reduces code duplication between column and row word extraction
 *
 * @param {Object} selectedTable - Selected table data
 * @param {number} currentPage - Current page number
 * @param {Function} shouldExtractCell - Function to determine if cell should be processed
 * @param {Function} getCellMetadata - Function to get additional metadata for extracted words
 * @param {Object} tableData - Table data for row index mapping
 * @returns {Array} Array of words with metadata
 */
const extractWordsFromCells = (
  selectedTable,
  currentPage,
  shouldExtractCell,
  getCellMetadata,
  tableData
) => {
  const words = [];

  Object.keys(selectedTable).forEach((cellKey) => {
    if (!isValidCellKey(cellKey)) return;

    const cellData = selectedTable[cellKey];
    if (!isCellOnPage(cellData, currentPage)) return;
    if (!cellData[0].words) return;

    if (shouldExtractCell(cellKey, cellData)) {
      const metadata = getCellMetadata(cellKey, cellData);

      cellData[0].words.forEach((word) => {
        words.push({
          ...word,
          originalCellKey: cellKey,
          ...metadata,
        });
      });
    }
  });

  return words;
};

/**
 * Extract words from affected cells for redistribution
 * Identifies cells affected by grid line movement and extracts their words with metadata
 *
 * @param {Object} selectedTable - Selected table data
 * @param {number} currentPage - Current page number
 * @param {Object} affectedRegions - Object containing affected columns and rows
 * @param {Object} movedGridLines - Object containing moved vertical and horizontal lines
 * @param {Object} tableData - Table data for row index mapping
 * @returns {Array} Array of words with metadata for redistribution
 */
const extractWordsFromAffectedCells = (
  selectedTable,
  currentPage,
  affectedRegions,
  movedGridLines,
  tableData
) => {
  const isColumnMovement =
    movedGridLines.vertical.length > 0 &&
    movedGridLines.horizontal.length === 0;
  const isRowMovement =
    movedGridLines.horizontal.length > 0 &&
    movedGridLines.vertical.length === 0;

  if (isColumnMovement) {
    return extractWordsFromCells(
      selectedTable,
      currentPage,
      (cellKey) => {
        const { colIndex } = parseCellKey(cellKey);
        return affectedRegions.columns.includes(colIndex);
      },
      (cellKey) => {
        const { rowIndex: selectedTableRowIndex, colIndex } = parseCellKey(
          cellKey
        );
        const tableRowIndex = findTableRowIndexForCell(
          cellKey,
          selectedTable,
          tableData,
          currentPage
        );
        return {
          originalTableCol: colIndex,
          originalTableRow: tableRowIndex,
          originalSelectedTableRow: selectedTableRowIndex,
        };
      },
      tableData
    );
  } else if (isRowMovement) {
    return extractWordsFromCells(
      selectedTable,
      currentPage,
      (cellKey) => {
        const mappedTableRowIndex = findTableRowIndexForCell(
          cellKey,
          selectedTable,
          tableData,
          currentPage
        );
        return affectedRegions.rows.includes(mappedTableRowIndex);
      },
      (cellKey) => {
        const { colIndex } = parseCellKey(cellKey);
        const tableRowIndex = findTableRowIndexForCell(
          cellKey,
          selectedTable,
          tableData,
          currentPage
        );
        return {
          originalTableRow: tableRowIndex,
          originalTableCol: colIndex,
        };
      },
      tableData
    );
  }

  return [];
};

/**
 * Clear cells that had words extracted (surgical clearing)
 * Only clears the specific cells where words were extracted, leaving other cells intact
 *
 * @param {Object} result - Table structure to clear cells in
 * @param {Array} wordsToRedistribute - Array of words with originalTableRow and originalTableCol metadata
 */
const clearAffectedCells = (result, wordsToRedistribute) => {
  const cellsToClear = new Set();

  wordsToRedistribute.forEach((word) => {
    if (
      word.originalTableRow !== null &&
      word.originalTableRow !== undefined &&
      word.originalTableCol !== null &&
      word.originalTableCol !== undefined
    ) {
      cellsToClear.add(`${word.originalTableRow}_${word.originalTableCol}`);
    }
  });

  cellsToClear.forEach((cellKey) => {
    const { rowIndex, colIndex } = parseCellKey(cellKey);

    if (
      rowIndex >= 0 &&
      rowIndex < result.rows.length &&
      colIndex >= 0 &&
      colIndex < result.rows[rowIndex].length
    ) {
      result.rows[rowIndex][colIndex] = "";
    }
  });
};

/**
 * Redistribute words to new positions and assign to cells
 * Determines new cell position for each word and updates table structure
 *
 * @param {Array} wordsToRedistribute - Words to redistribute
 * @param {Object} movedGridLines - Moved grid line data
 * @param {Object} newGridLines - New grid line positions
 * @param {Object} pdfPageDimensions - PDF page dimensions
 * @param {Object} tableData - Table structure
 * @param {boolean} hasLeftEdgeInArray - Whether grid lines include left edge
 * @param {boolean} hasTopEdgeInArray - Whether grid lines include top edge
 * @param {number} pageRowOffset - Row offset for multi-page tables
 * @param {Object} result - Table structure to update
 * @returns {Object} Cell word map for selectedTable updates
 */
const redistributeWordsToNewPositions = (
  wordsToRedistribute,
  movedGridLines,
  newGridLines,
  pdfPageDimensions,
  tableData,
  hasLeftEdgeInArray,
  hasTopEdgeInArray,
  pageRowOffset,
  result
) => {
  const cellWordMap = {};

  wordsToRedistribute.forEach((word) => {
    try {
      // Skip header words
      if (
        word.originalTableRow === null ||
        word.originalSelectedTableRow === 0
      ) {
        return;
      }

      const newPosition = determineNewCellPosition(
        word,
        movedGridLines,
        newGridLines,
        pdfPageDimensions,
        tableData,
        hasLeftEdgeInArray,
        hasTopEdgeInArray,
        pageRowOffset
      );

      if (
        newPosition &&
        newPosition.row >= 0 &&
        newPosition.row < result.rows.length &&
        newPosition.col >= 0 &&
        newPosition.col < result.rows[newPosition.row].length
      ) {
        const cellKey = `${newPosition.row}_${newPosition.col}`;
        if (!cellWordMap[cellKey]) {
          cellWordMap[cellKey] = [];
        }
        cellWordMap[cellKey].push(word);
      }
    } catch (error) {
      // Silently skip words that fail to redistribute
    }
  });

  // Assign words to cells
  Object.keys(cellWordMap).forEach((cellKey) => {
    const { rowIndex, colIndex } = parseCellKey(cellKey);
    const sortedWords = sortWordsByPosition(cellWordMap[cellKey]);
    const text = sortedWords
      .map((w) => w.text)
      .join(" ")
      .trim();

    if (
      rowIndex >= 0 &&
      rowIndex < result.rows.length &&
      colIndex >= 0 &&
      colIndex < result.rows[rowIndex].length
    ) {
      result.rows[rowIndex][colIndex] = text;
    }
  });

  return cellWordMap;
};

/**
 * Clear and redistribute words after grid line movements
 * Orchestrates the three-step process: extract words, clear cells, redistribute words
 *
 * @param {Object} tableData - Current table structure
 * @param {Object} affectedRegions - Regions affected by grid line movement
 * @param {Object} movedGridLines - Moved grid line data
 * @param {Object} selectedTable - Selected table data
 * @param {Object} newGridLines - New grid line positions
 * @param {Object} pdfPageDimensions - PDF page dimensions
 * @param {number} currentPage - Current page number
 * @param {boolean} hasLeftEdgeInArray - Whether vertical grid lines include left edge
 * @param {boolean} hasTopEdgeInArray - Whether horizontal grid lines include top edge
 * @param {number} pageRowOffset - Row offset for multi-page tables
 * @returns {Object} Object with result (table structure) and cellWordMap (for selectedTable updates)
 */
const clearAndRedistribute = (
  tableData,
  affectedRegions,
  movedGridLines,
  selectedTable,
  newGridLines,
  pdfPageDimensions,
  currentPage,
  hasLeftEdgeInArray = false,
  hasTopEdgeInArray = false,
  pageRowOffset = 0
) => {
  const result = {
    headers: [...tableData.headers],
    rows: tableData.rows.map((row) => [...row]),
  };

  const wordsToRedistribute = extractWordsFromAffectedCells(
    selectedTable,
    currentPage,
    affectedRegions,
    movedGridLines,
    tableData
  );

  clearAffectedCells(result, wordsToRedistribute);

  const cellWordMap = redistributeWordsToNewPositions(
    wordsToRedistribute,
    movedGridLines,
    newGridLines,
    pdfPageDimensions,
    tableData,
    hasLeftEdgeInArray,
    hasTopEdgeInArray,
    pageRowOffset,
    result
  );

  return { result, cellWordMap };
};

/**
 * Calculate new position for a dimension (row or column) based on moved grid line
 * Unifies calculation logic for both row and column movements
 *
 * @param {Object} wordCenter - Word center coordinates {x, y}
 * @param {Object} movement - Movement object with index and newPos
 * @param {boolean} isRow - True for row calculation, false for column
 * @param {boolean} hasEdgeInArray - Whether grid line array includes edge
 * @param {number} pageRowOffset - Row offset for multi-page tables (0 for columns)
 * @param {number} maxIndex - Maximum valid index
 * @returns {number} New row or column index
 */
const calculateNewDimensionPosition = (
  wordCenter,
  movement,
  isRow,
  hasEdgeInArray,
  pageRowOffset,
  maxIndex
) => {
  const gridLinePosition = movement.newPos;
  const coordinate = isRow ? wordCenter.y : wordCenter.x;
  let newIndex;

  if (hasEdgeInArray) {
    newIndex =
      coordinate < gridLinePosition
        ? Math.max(0, movement.index - 1)
        : movement.index;
  } else {
    newIndex =
      coordinate < gridLinePosition
        ? Math.max(0, movement.index)
        : movement.index + 1;
  }

  if (isRow) {
    newIndex = newIndex + pageRowOffset;
  }

  return Math.max(0, Math.min(newIndex, maxIndex - 1));
};

/**
 * Determine new cell position for a word based on moved grid lines
 * Uses simple table row/column indices (not selectedTable global indices)
 */
const determineNewCellPosition = (
  word,
  movedGridLines,
  newGridLines,
  pdfPageDimensions,
  tableData,
  hasLeftEdgeInArray = false,
  hasTopEdgeInArray = false,
  pageRowOffset = 0
) => {
  const wordCenter = calculateWordCenter(word, pdfPageDimensions);
  let newRow = word.originalTableRow;
  let newCol = word.originalTableCol;

  if (movedGridLines.horizontal.length > 0) {
    for (const movement of movedGridLines.horizontal) {
      newRow = calculateNewDimensionPosition(
        wordCenter,
        movement,
        true,
        hasTopEdgeInArray,
        pageRowOffset,
        tableData.rows.length
      );
    }
  }

  if (movedGridLines.vertical.length > 0) {
    for (const movement of movedGridLines.vertical) {
      newCol = calculateNewDimensionPosition(
        wordCenter,
        movement,
        false,
        hasLeftEdgeInArray,
        0,
        tableData.headers.length
      );
    }
  }

  if (
    newRow >= 0 &&
    newRow < tableData.rows.length &&
    newCol >= 0 &&
    newCol < tableData.headers.length
  ) {
    return { row: newRow, col: newCol };
  }

  return null;
};

// ============================================================================
// MAIN RECALCULATION FUNCTIONS
// ============================================================================

/**
 * Simplified gridline movement logic that works for all table types
 * Handles grid line movements (not additions/removals) for both single-page and multi-page tables
 */
const recalculateCellsFromGridLinesForMovements = (
  dynamicGridLines,
  currentPage,
  selectedTable,
  tableData,
  originalGridLines,
  pdfPageDimensions,
  passedOriginalGridLines = null,
  passedNewGridLines = null
) => {
  if (!dynamicGridLines?.[currentPage] || !selectedTable || !tableData) {
    return null;
  }

  const newGridLines =
    passedNewGridLines?.[currentPage] || dynamicGridLines[currentPage];
  const originalGridLinesForPage = (passedOriginalGridLines ||
    originalGridLines)?.[currentPage];

  if (!originalGridLinesForPage || !newGridLines) {
    return tableData;
  }

  const movedGridLines = detectMovedGridLines(
    originalGridLinesForPage,
    newGridLines
  );

  if (!movedGridLines.hasMovement) {
    return tableData;
  }

  const totalVerticalGridLines = newGridLines.vertical?.length || 0;
  const totalHorizontalGridLines = newGridLines.horizontal?.length || 0;

  const affectedRegions = findAffectedTableRegions(
    movedGridLines,
    tableData,
    selectedTable,
    currentPage,
    totalVerticalGridLines,
    totalHorizontalGridLines
  );

  const hasLeftEdgeInArray =
    totalVerticalGridLines === tableData.headers.length + 1;
  const hasTopEdgeInArray =
    totalHorizontalGridLines === tableData.rows.length + 1;
  const pageRowOffset = calculatePageRowOffset(selectedTable, currentPage);

  const { result, cellWordMap } = clearAndRedistribute(
    tableData,
    affectedRegions,
    movedGridLines,
    selectedTable,
    newGridLines,
    pdfPageDimensions,
    currentPage,
    hasLeftEdgeInArray,
    hasTopEdgeInArray,
    pageRowOffset
  );

  result.__movedLines = {
    vertical: affectedRegions.columns,
    horizontal: affectedRegions.rowIds,
  };

  result.__cellWordMap = cellWordMap;
  result.__currentPage = currentPage;

  return result;
};

/**
 * Main recalculation function - routes between movement and structural change processing
 *
 * Determines if changes are movements (same number of grid lines) or structural changes
 * (added/removed grid lines) and routes to appropriate processing function.
 */
export const recalculateCellsFromGridLines = (
  dynamicGridLines,
  currentPage,
  selectedTable,
  tableData,
  originalGridLines,
  pdfPageDimensions,
  passedOriginalGridLines = null,
  passedNewGridLines = null,
  limitColumnAdditionsToCurrentPage = !COLUMN_ADDITION_MULTI_PAGE,
  limitColumnMovementsToCurrentPage = !COLUMN_MOVEMENT_MULTI_PAGE
) => {
  const originalGridLinesForPage = (passedOriginalGridLines ||
    originalGridLines)?.[currentPage];
  const newGridLinesForPage = (passedNewGridLines || dynamicGridLines)?.[
    currentPage
  ];

  if (originalGridLinesForPage && newGridLinesForPage) {
    const originalVerticalCount =
      originalGridLinesForPage.vertical?.length || 0;
    const newVerticalCount = newGridLinesForPage.vertical?.length || 0;
    const originalHorizontalCount =
      originalGridLinesForPage.horizontal?.length || 0;
    const newHorizontalCount = newGridLinesForPage.horizontal?.length || 0;

    if (
      originalVerticalCount === newVerticalCount &&
      originalHorizontalCount === newHorizontalCount
    ) {
      return recalculateCellsFromGridLinesForMovements(
        dynamicGridLines,
        currentPage,
        selectedTable,
        tableData,
        originalGridLines,
        pdfPageDimensions,
        passedOriginalGridLines,
        passedNewGridLines
      );
    }
  }

  const newGridLines = passedNewGridLines || dynamicGridLines;
  const originalGridLinesData = passedOriginalGridLines || originalGridLines;

  const validation = validateRecalculationInputs(
    dynamicGridLines,
    currentPage,
    selectedTable,
    tableData,
    originalGridLinesData,
    newGridLines
  );
  if (!validation.isValid) {
    return validation.result;
  }

  const allChanges = detectStructuralGridLineChanges(
    originalGridLinesData,
    newGridLines,
    selectedTable,
    pdfPageDimensions
  );
  if (allChanges.length === 0) return tableData;

  const relevantChanges = allChanges.filter((change) =>
    shouldIncludeChange(
      change,
      currentPage,
      limitColumnAdditionsToCurrentPage,
      limitColumnMovementsToCurrentPage
    )
  );
  if (relevantChanges.length === 0) return tableData;

  const targetPages = getTargetPages(
    relevantChanges,
    currentPage,
    limitColumnAdditionsToCurrentPage,
    limitColumnMovementsToCurrentPage
  );
  const affectedCells = getAffectedCells(
    relevantChanges,
    selectedTable,
    targetPages
  );
  if (affectedCells.size === 0) return tableData;

  const wordsToRedistribute = extractWordsFromPages(
    selectedTable,
    targetPages
  ).filter((word) => affectedCells.has(word.originalCellKey));
  if (wordsToRedistribute.length === 0) return tableData;

  const newTableStructure = createNewTableStructure(
    tableData,
    relevantChanges,
    selectedTable
  );
  const wordAssignments = redistributeWords(
    wordsToRedistribute,
    newGridLines,
    relevantChanges,
    newTableStructure,
    selectedTable,
    pdfPageDimensions,
    currentPage
  );
  const finalResult = applyWordAssignments(
    newTableStructure,
    wordAssignments,
    selectedTable,
    relevantChanges
  );

  return addUIMetadata(finalResult, relevantChanges);
};
